/**
 * useListSwipeGesture - Pan gesture configured for "swipe horizontally to
 * navigate between sibling tabs/lists." Filters out vertical drags so it
 * coexists with vertical scroll views and drag-to-reorder.
 *
 * Tuning constants are exported so callers can tweak per-screen if needed.
 */

import { useMemo } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';

export const SWIPE_HORIZONTAL_ACTIVATE_PX = 15;
export const SWIPE_VERTICAL_FAIL_PX = 12;
export const SWIPE_MIN_VELOCITY = 200;
export const SWIPE_MIN_DISTANCE = 40;

type UseListSwipeGestureOptions = {
  onSwipeLeft?: () => void;   // user dragged finger right→left → go to next
  onSwipeRight?: () => void;  // user dragged finger left→right → go to previous
  enabled?: boolean;
};

export function useListSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
}: UseListSwipeGestureOptions) {
  return useMemo(() => {
    const gesture = Gesture.Pan()
      .activeOffsetX([-SWIPE_HORIZONTAL_ACTIVATE_PX, SWIPE_HORIZONTAL_ACTIVATE_PX])
      .failOffsetY([-SWIPE_VERTICAL_FAIL_PX, SWIPE_VERTICAL_FAIL_PX])
      .enabled(enabled)
      .onEnd((event) => {
        const { translationX, velocityX } = event;
        const fastEnough = Math.abs(velocityX) >= SWIPE_MIN_VELOCITY;
        const farEnough = Math.abs(translationX) >= SWIPE_MIN_DISTANCE;
        if (!fastEnough && !farEnough) return;
        if (translationX < 0 && onSwipeLeft) {
          runOnJS(onSwipeLeft)();
        } else if (translationX > 0 && onSwipeRight) {
          runOnJS(onSwipeRight)();
        }
      });
    return gesture;
  }, [onSwipeLeft, onSwipeRight, enabled]);
}
